import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Stack,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ChevronLeft,
  ExpandMore,
  FolderOpen,
  Person,
  Note,
  SportsEsports,
  AccountTree,
  GridOn,
  Functions,
} from '@mui/icons-material';
import FileTree from './FileTree';
import useThemeStyles from '../../../../hooks/useThemeStyles';
import { getProjectTypeConfig } from '../../../../utils/projectTypeConfig';

const ProjectNavigation = ({
  activeProject = null,
  chapters,
  handleAddChapter,
  handleChapterClick = null,
  handleAddScene,
  handleSceneClick = null,
  setRefreshScenesCallback,
  onToggle,
  onExport,
  onImport,
  onShare,
  activeSection,
  onSectionChange,
  onGameToolSelect = null,
  activeGameTool = 'erd',
  characters = [],
  notes = [],
}) => {
  const { colors } = useThemeStyles();
  const [fileTreeExpanded, setFileTreeExpanded] = useState(activeSection === 'manuscript');
  const [charactersExpanded, setCharactersExpanded] = useState(activeSection === 'characters');
  const [notesExpanded, setNotesExpanded] = useState(activeSection === 'notes');
  const [gameDesignExpanded, setGameDesignExpanded] = useState(activeSection === 'gamedesign');
  const typeConfig = getProjectTypeConfig(activeProject?.type);
  const isVideoGameProject = typeConfig.showGameDesign;

  useEffect(() => {
    setFileTreeExpanded(activeSection === 'manuscript');
    setCharactersExpanded(activeSection === 'characters');
    setNotesExpanded(activeSection === 'notes');
    setGameDesignExpanded(activeSection === 'gamedesign');
  }, [activeSection]);

  const gameDesignTools = [
    { id: 'erd', label: 'ERD', icon: AccountTree },
    { id: 'types', label: 'Type Chart', icon: GridOn },
    { id: 'formulas', label: 'Formulas', icon: Functions },
  ];

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'auto',
        backgroundColor: colors.sidebar.background,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header with Navigation label and Collapse */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: `1px solid ${colors.border.subtle}`,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: colors.text.primary,
            fontSize: '1rem',
            fontWeight: 600,
            letterSpacing: '-0.01em',
          }}
        >
          Navigation
        </Typography>
        <Button
          onClick={onToggle}
          size="small"
          endIcon={<ChevronLeft sx={{ fontSize: '1.1rem' }} />}
          sx={{
            backgroundColor: 'transparent',
            color: colors.text.secondary,
            borderRadius: '8px',
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 500,
            px: 1.5,
            py: 0.75,
            minWidth: 'auto',
            '&:hover': {
              backgroundColor: colors.sidebar.headerHover,
              color: colors.text.primary,
            },
          }}
        >
          Collapse
        </Button>
      </Box>

      <Box sx={{ p: 2, flex: 1 }}>
        <Stack spacing={1.5}>
          {/* File Tree Navigation Accordion */}
          <Accordion
            expanded={fileTreeExpanded}
            onChange={(_, isExpanded) => {
              setFileTreeExpanded(isExpanded);
              if (isExpanded) {
                setCharactersExpanded(false);
                setNotesExpanded(false);
                if (onSectionChange) {
                  onSectionChange('manuscript');
                }
              }
            }}
            sx={{
              backgroundColor: 'transparent',
              border: 'none',
              boxShadow: 'none',
              '&:before': { display: 'none' },
              '&.Mui-expanded': { margin: 0 },
              margin: 0,
            }}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMore
                  sx={{
                    color: colors.text.secondary,
                    fontSize: '1.25rem',
                  }}
                />
              }
              sx={{
                backgroundColor: 'transparent',
                color: colors.text.primary,
                minHeight: '44px',
                px: 2,
                py: 1,
                borderRadius: '8px',
                '&.Mui-expanded': {
                  minHeight: '44px',
                  backgroundColor: colors.sidebar.headerBackground,
                },
                '& .MuiAccordionSummary-content': {
                  margin: 0,
                  alignItems: 'center',
                  '&.Mui-expanded': {
                    margin: 0,
                  },
                },
                '&:hover': {
                  backgroundColor: colors.sidebar.headerHover,
                },
              }}
            >
              <FolderOpen
                sx={{
                  color: colors.interactive.primary,
                  marginRight: 1.5,
                  fontSize: '1.25rem',
                }}
              />
              <Typography
                variant="body1"
                sx={{
                  color: colors.text.primary,
                  fontSize: '0.9375rem',
                  fontWeight: 500,
                }}
              >
                {typeConfig.sectionLabel}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0, backgroundColor: 'transparent' }}>
              <Box sx={{ px: 2, py: 1 }}>
                <FileTree
                  activeProject={activeProject}
                  chapters={chapters}
                  onAddChapter={handleAddChapter}
                  onChapterClick={handleChapterClick}
                  onAddScene={handleAddScene}
                  onSceneClick={handleSceneClick}
                  setRefreshScenesCallback={setRefreshScenesCallback}
                  onExport={onExport}
                  onImport={onImport}
                  onShare={onShare}
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Characters Accordion (hidden for types without characters, e.g. poetry/essay) */}
          {typeConfig.showCharacters && (
            <Accordion
              expanded={charactersExpanded}
              onChange={(_, isExpanded) => {
                setCharactersExpanded(isExpanded);
                setFileTreeExpanded(false);
                setNotesExpanded(false);
                // Always navigate to characters page when accordion is interacted with
                if (onSectionChange) {
                  onSectionChange('characters');
                }
              }}
              sx={{
                backgroundColor: 'transparent',
                border: 'none',
                boxShadow: 'none',
                '&:before': { display: 'none' },
                '&.Mui-expanded': { margin: 0 },
                margin: 0,
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMore
                    sx={{
                      color: colors.text.secondary,
                      fontSize: '1.25rem',
                    }}
                  />
                }
                sx={{
                  backgroundColor: 'transparent',
                  color: colors.text.primary,
                  minHeight: '44px',
                  px: 2,
                  py: 1,
                  borderRadius: '8px',
                  '&.Mui-expanded': {
                    minHeight: '44px',
                    backgroundColor: colors.sidebar.headerBackground,
                  },
                  '& .MuiAccordionSummary-content': {
                    margin: 0,
                    alignItems: 'center',
                    '&.Mui-expanded': {
                      margin: 0,
                    },
                  },
                  '&:hover': {
                    backgroundColor: colors.sidebar.headerHover,
                  },
                }}
              >
                <Person
                  sx={{
                    color: colors.interactive.primary,
                    marginRight: 1.5,
                    fontSize: '1.25rem',
                  }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    color: colors.text.primary,
                    fontSize: '0.9375rem',
                    fontWeight: 500,
                  }}
                >
                  Characters
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0, backgroundColor: 'transparent' }}>
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.text.secondary,
                      fontSize: '0.875rem',
                    }}
                  >
                    {characters.length > 0
                      ? `${characters.length} character${characters.length === 1 ? '' : 's'} in this project.`
                      : 'No characters have been added yet.'}
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Notes Accordion */}
          <Accordion
            expanded={notesExpanded}
            onChange={(_, isExpanded) => {
              setNotesExpanded(isExpanded);
              if (isExpanded) {
                setFileTreeExpanded(false);
                setCharactersExpanded(false);
                if (onSectionChange) {
                  onSectionChange('notes');
                }
              }
            }}
            sx={{
              backgroundColor: 'transparent',
              border: 'none',
              boxShadow: 'none',
              '&:before': { display: 'none' },
              '&.Mui-expanded': { margin: 0 },
              margin: 0,
            }}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMore
                  sx={{
                    color: colors.text.secondary,
                    fontSize: '1.25rem',
                  }}
                />
              }
              sx={{
                backgroundColor: 'transparent',
                color: colors.text.primary,
                minHeight: '44px',
                px: 2,
                py: 1,
                borderRadius: '8px',
                '&.Mui-expanded': {
                  minHeight: '44px',
                  backgroundColor: colors.sidebar.headerBackground,
                },
                '& .MuiAccordionSummary-content': {
                  margin: 0,
                  alignItems: 'center',
                  '&.Mui-expanded': {
                    margin: 0,
                  },
                },
                '&:hover': {
                  backgroundColor: colors.sidebar.headerHover,
                },
              }}
            >
              <Note
                sx={{
                  color: colors.interactive.primary,
                  marginRight: 1.5,
                  fontSize: '1.25rem',
                }}
              />
              <Typography
                variant="body1"
                sx={{
                  color: colors.text.primary,
                  fontSize: '0.9375rem',
                  fontWeight: 500,
                }}
              >
                Notes
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0, backgroundColor: 'transparent' }}>
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.text.secondary,
                    fontSize: '0.875rem',
                  }}
                >
                  {notes.length > 0
                    ? `${notes.length} note${notes.length === 1 ? '' : 's'} in this project.`
                    : 'No notes yet — use the Notes panel to add one.'}
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Game Design Accordion (video game projects only) */}
          {isVideoGameProject && (
            <Accordion
              expanded={gameDesignExpanded}
              onChange={(_, isExpanded) => {
                setGameDesignExpanded(isExpanded);
                if (isExpanded) {
                  setFileTreeExpanded(false);
                  setCharactersExpanded(false);
                  setNotesExpanded(false);
                  if (onSectionChange) {
                    onSectionChange('gamedesign');
                  }
                }
              }}
              sx={{
                backgroundColor: 'transparent',
                border: 'none',
                boxShadow: 'none',
                '&:before': { display: 'none' },
                '&.Mui-expanded': { margin: 0 },
                margin: 0,
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMore
                    sx={{
                      color: colors.text.secondary,
                      fontSize: '1.25rem',
                    }}
                  />
                }
                sx={{
                  backgroundColor: 'transparent',
                  color: colors.text.primary,
                  minHeight: '44px',
                  px: 2,
                  py: 1,
                  borderRadius: '8px',
                  '&.Mui-expanded': {
                    minHeight: '44px',
                    backgroundColor: colors.sidebar.headerBackground,
                  },
                  '& .MuiAccordionSummary-content': {
                    margin: 0,
                    alignItems: 'center',
                    '&.Mui-expanded': {
                      margin: 0,
                    },
                  },
                  '&:hover': {
                    backgroundColor: colors.sidebar.headerHover,
                  },
                }}
              >
                <SportsEsports
                  sx={{
                    color: colors.interactive.primary,
                    marginRight: 1.5,
                    fontSize: '1.25rem',
                  }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    color: colors.text.primary,
                    fontSize: '0.9375rem',
                    fontWeight: 500,
                  }}
                >
                  Game Design
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0, backgroundColor: 'transparent' }}>
                <Stack spacing={0.5} sx={{ px: 2, py: 1 }}>
                  {gameDesignTools.map((tool) => {
                    const ToolIcon = tool.icon;
                    const isSelected = activeSection === 'gamedesign' && activeGameTool === tool.id;
                    return (
                      <Button
                        key={tool.id}
                        onClick={() => onGameToolSelect && onGameToolSelect(tool.id)}
                        startIcon={
                          <ToolIcon
                            sx={{ fontSize: '1.1rem', color: colors.interactive.primary }}
                          />
                        }
                        sx={{
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          color: colors.text.primary,
                          backgroundColor: isSelected
                            ? colors.sidebar.headerBackground
                            : 'transparent',
                          borderRadius: '6px',
                          px: 1.5,
                          '&:hover': {
                            backgroundColor: colors.sidebar.headerHover,
                          },
                        }}
                      >
                        {tool.label}
                      </Button>
                    );
                  })}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

/* eslint-disable react/require-default-props */
ProjectNavigation.propTypes = {
  activeProject: PropTypes.shape({
    _id: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.string,
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string,
    thinOwner: PropTypes.shape({
      name: PropTypes.string,
      id: PropTypes.string,
    }),
    thinCoauthors: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        id: PropTypes.string,
      })
    ),
    thinEditors: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        id: PropTypes.string,
      })
    ),
  }),
  chapters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    })
  ).isRequired,
  handleAddChapter: PropTypes.func.isRequired,
  handleChapterClick: PropTypes.func,
  handleAddScene: PropTypes.func.isRequired,
  handleSceneClick: PropTypes.func,
  setRefreshScenesCallback: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  onExport: PropTypes.func,
  onImport: PropTypes.func,
  onShare: PropTypes.func,
  activeSection: PropTypes.string,
  onSectionChange: PropTypes.func,
  onGameToolSelect: PropTypes.func,
  activeGameTool: PropTypes.string,
  characters: PropTypes.arrayOf(PropTypes.shape({})),
  notes: PropTypes.arrayOf(PropTypes.shape({})),
};
/* eslint-enable react/require-default-props */

export default ProjectNavigation;
